import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger, } from "@/components/ui/popover";
import { CUSTOM_DATE_RANGE_LABEL_FORMAT, QUICK_RANGES } from "@/dateRanges/dateRanges";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { AlertBox } from "./alert-box";
import { Combobox } from "./combo-box";
import { Separator } from "./ui/separator";
import { useDateRange } from "@/dateRanges/DateRangeContext";
import { AUTO, WINDOW_SIZES } from "@/dateRanges/windowSizes";
import { OFF, REFRESH_INTERVALS } from "@/dateRanges/refreshIntervals";
import { useIsMobile } from "@/hooks/use-mobile";

export function CalendarDateRangePicker(
  {
    className,
    align,
    showExtraOptions = true,
  }: any
) {
  const ctx = useDateRange();
  const isMobile = useIsMobile();

  const [to, setTo] = useState<Date | undefined>(ctx.dateRange.value.to())
  const [from, setFrom] = useState<Date | undefined>(ctx.dateRange.value.from())
  const [live, setLive] = useState(ctx.dateRange.value.live)

  const [windowSize, setWindowSize] = useState(ctx.windowSize);
  const [refreshInterval, setRefreshInterval] = useState(ctx.refreshInterval);

  const [label, setLabel] = useState(ctx.dateRange.label);

  useEffect(() => {
    setTo(ctx.dateRange.value.to())
    setFrom(ctx.dateRange.value.from())
    setWindowSize(ctx.windowSize)
    setRefreshInterval(ctx.refreshInterval)
    setLabel(ctx.dateRange.label)
  }, [ctx.dateRange, ctx.windowSize, ctx.refreshInterval])

  const [open, setOpen] = useState(false);

  const apply = () => {
    if (to && from) {
      ctx.setRefreshInterval(refreshInterval);
      ctx.setWindowSize(windowSize);

      // If the selected label matches a quick range, use that predefined range
      const selectedQuickRange = QUICK_RANGES.find(range => range.label === label);

      if (selectedQuickRange) {
        console.log("quick range selected")
        ctx.setDateRange(selectedQuickRange);
      } else {
        console.log("static range selected")
        ctx.setDateRange({
          label,
          value: {
            to: () => to,
            from: () => from,
            live,
          }
        });
      }
      setOpen(false);
    }
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            size="sm"
            className={cn(
              "w-full sm:w-auto justify-start bg-white text-left font-normal",
              !(from || to) && "text-muted-foreground",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4"/>
            <span className="truncate">{label ? label : "Pick a date"}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="flex flex-col sm:flex-row min-h-[355px] w-auto p-0 max-w-[95vw]"
          side="bottom"
          align={align || "start"}
        >
          <Calendar
            className="p-3 text-thalo-foreground"
            initialFocus
            mode="range"
            defaultMonth={from}
            selected={{ from, to }}
            onSelect={(val) => {
              setTo(val?.to)
              setFrom(val?.from)
              setLive(false)
              if (val?.to && val?.from) {
                setLabel(`${format(val.from, CUSTOM_DATE_RANGE_LABEL_FORMAT)} - ${format(val.to, CUSTOM_DATE_RANGE_LABEL_FORMAT)}`)
              } else if (val?.from) {
                setLabel(`${format(val.from, CUSTOM_DATE_RANGE_LABEL_FORMAT)}`)
              }
            }}
            numberOfMonths={isMobile ? 1 : 2}
          />
          <div className="flex w-full sm:max-w-[224px] flex-col p-3">
            <div className="flex flex-1 flex-col gap-3 text-thalo-foreground">
              <Combobox
                placeholder="Quick ranges"
                options={QUICK_RANGES}
                onSelectOption={(r) => {
                  setTo(r.value.to());
                  setFrom(r.value.from());
                  setLive(r.value.live);
                  if (!r.value.live) {
                    setRefreshInterval(OFF);
                  }
                  setLabel(r.label);
                }}
                defaultLabel={ctx.dateRange.label}
              />
              {showExtraOptions && (
                <>
                  <Separator/>
                  <div className="text-thalo-foreground">
                    <div className="ml-1 text-xs">Refresh Interval</div>
                    <Combobox
                      placeholder="Refresh interval"
                      options={REFRESH_INTERVALS}
                      onSelectOption={(option) => setRefreshInterval(option)}
                      defaultValue={refreshInterval.value}
                    />
                  </div>
                  <div className="text-thalo-foreground">
                    <div className="ml-1 text-xs">Window size</div>
                    <Combobox
                      placeholder="Window size"
                      options={WINDOW_SIZES}
                      onSelectOption={(option) => setWindowSize(option)}
                      defaultValue={windowSize.value}
                    />
                    {windowSize !== AUTO && (
                      <AlertBox
                        className="my-1"
                        variant="destructive"
                        hideIcon={true}
                        description="Consider the granularity of the query when using a small window size with a large time range. If you are not sure select 'Auto'."
                      />
                    )}
                  </div>
                </>
              )}
            </div>
            <Button
              disabled={!from || !to}
              onClick={apply}
              className="w-full mt-2"
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}